/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect, useState } from 'react';
import { useLocation, useNavigation } from 'react-router';
import { CategoryGridSkeleton } from '@/components/category-skeleton';
import ProductContentSkeleton from '@/components/product-skeleton';
import { matchNavSkeletonKind, type NavSkeletonKind } from './match';

function renderSkeleton(kind: Exclude<NavSkeletonKind, null>) {
    if (kind === 'pdp') {
        return (
            <div className="section-container pt-8 pb-16">
                <ProductContentSkeleton />
            </div>
        );
    }
    return <CategoryGridSkeleton />;
}

/**
 * Destination-shaped skeleton shown during a *blocking* navigation (one whose loader `await`s
 * critical data, e.g. the PDP) once it has been pending long enough that the top progress bar
 * would otherwise appear to hang. React Router hasn't committed the new URL yet —
 * `navigation.location` holds the pending destination — so we shape the skeleton from that path
 * and cover the content area, giving the shopper the layout they're heading to instead of a page
 * that looks stuck. When the loader resolves the URL commits, the real page renders, and this
 * overlay unmounts.
 *
 * Scope is deliberately narrow: only the top-of-funnel routes in {@link matchNavSkeletonKind}
 * (PDP, category, search) get an overlay. Any other destination — cart, account, checkout, home,
 * content pages — resolves to `null`, so this overlay never leaks a product-grid skeleton onto a
 * route that isn't a product grid. Those routes rely on the top progress bar alone.
 *
 * Instant-URL routes commit before `delayMs` and render their own Suspense skeleton, so their
 * pending window closes before this overlay would show — no double skeleton.
 */
export default function NavSkeletonOverlay({ delayMs = 400 }: { delayMs?: number }) {
    const navigation = useNavigation();
    const location = useLocation();
    const pendingPath =
        navigation.state === 'loading' && navigation.location && navigation.location.pathname !== location.pathname
            ? navigation.location.pathname
            : null;
    const kind = pendingPath ? matchNavSkeletonKind(pendingPath) : null;

    // Only reveal after `delayMs` so quick navigations never flash a skeleton; a new pending
    // kind restarts the timer, and returning to idle (or a non-funnel destination) clears it.
    const [shownKind, setShownKind] = useState<NavSkeletonKind>(null);
    useEffect(() => {
        if (!kind) {
            setShownKind(null);
            return undefined;
        }
        const t = setTimeout(() => setShownKind(kind), delayMs);
        return () => clearTimeout(t);
    }, [kind, delayMs]);

    if (!shownKind) return null;
    return (
        <div
            aria-hidden="true"
            className="fixed inset-x-0 bottom-0 z-40 overflow-y-auto bg-background"
            style={{ top: 'var(--header-height, 105px)' }}>
            {renderSkeleton(shownKind)}
        </div>
    );
}
